import { toast } from '@/components/ui/use-toast';

// Error categories for better organization
export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  DATABASE = 'database',
  NETWORK = 'network',
  VALIDATION = 'validation',
  EXTERNAL_SERVICE = 'external_service',
  UNEXPECTED = 'unexpected'
}

// Error severity levels
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// For backward compatibility with existing code
export interface ErrorContext {
  operation?: string;
  source?: string;
  userId?: string;
  organizationId?: string;
  category?: ErrorCategory;
  additionalInfo?: Record<string, unknown>;
  [key: string]: unknown;
}

interface ErrorMetadata {
  userId?: string;
  organizationId?: string;
  category?: ErrorCategory;
  componentName?: string;
  operationName?: string;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
  [key: string]: unknown;
}

interface ErrorTrackingOptions {
  showToast?: boolean;
  severity?: ErrorSeverity;
  additionalData?: Record<string, unknown>;
}

// Production-ready error handling class
export class ErrorHandler {
  private componentName: string;
  private isDevelopment: boolean;
  
  constructor(componentName: string) {
    this.componentName = componentName;
    this.isDevelopment = import.meta.env.MODE === 'development';
  }
  
  /**
   * Centralized error handling with optional reporting
   */
  handleError(
    error: unknown,
    message: string = 'An unexpected error occurred',
    metadata: ErrorMetadata = {},
    showToast: boolean = true,
    severity: ErrorSeverity = ErrorSeverity.ERROR
  ): Error {
    // Ensure we're working with an Error object
    const errorObject = error instanceof Error ? error : new Error(
      typeof error === 'string' ? error : message
    );
    
    // Normalize metadata
    const normalizedMetadata = {
      ...metadata,
      componentName: metadata.componentName || this.componentName,
      timestamp: new Date().toISOString()
    };
    
    // In development, log to console for debugging
    if (this.isDevelopment) {
      // Group related errors in console
      console.group(`Error in ${normalizedMetadata.componentName}`);
      console.error(message, errorObject);
      console.info('Metadata:', normalizedMetadata);
      console.groupEnd();
    }
    
    // Send to error tracking service in production
    this.reportToErrorService(errorObject, message, normalizedMetadata, severity);
    
    // Show user-friendly toast if requested
    if (showToast) {
      this.showErrorToast(message, severity);
    }
    
    return errorObject;
  }
  
  /**
   * Report error to monitoring service
   * You would replace this with your actual error reporting service
   */
  private reportToErrorService(
    error: Error,
    message: string,
    metadata: ErrorMetadata,
    severity: ErrorSeverity
  ): void {
    // Don't send to error service in development mode
    if (this.isDevelopment) {
      return;
    }
    
    // Production error reporting
    try {
      // Implementation for your chosen error reporting service
      // e.g., Sentry, LogRocket, New Relic, etc.
      if (window.errorReportingService?.captureException) {
        window.errorReportingService.captureException(error, {
          extra: metadata,
          tags: {
            component: metadata.componentName,
            severity: severity.toString(),
            category: metadata.category ? metadata.category.toString() : ErrorCategory.UNEXPECTED.toString()
          },
          level: this.mapSeverityToReportingLevel(severity),
          contexts: {
            operation: {
              name: metadata.operationName || 'unknown',
              organizationId: metadata.organizationId,
              userId: metadata.userId
            }
          }
        });
      }
    } catch (reportingError) {
      // Silently fail if error reporting itself fails
      if (this.isDevelopment) {
        console.error('Error reporting failed:', reportingError);
      }
    }
  }
  
  /**
   * Map our severity levels to reporting service levels
   */
  private mapSeverityToReportingLevel(severity: ErrorSeverity): string {
    switch (severity) {
      case ErrorSeverity.INFO:
        return 'info';
      case ErrorSeverity.WARNING:
        return 'warning';
      case ErrorSeverity.CRITICAL:
        return 'fatal';
      case ErrorSeverity.ERROR:
      default:
        return 'error';
    }
  }
  
  /**
   * Display user-friendly error toast
   */
  private showErrorToast(message: string, severity: ErrorSeverity): void {
    const variant = this.getSeverityVariant(severity);
    
    toast({
      title: this.getSeverityTitle(severity),
      description: message,
      variant
    });
  }
  
  /**
   * Get toast variant based on severity
   */
  private getSeverityVariant(severity: ErrorSeverity): 'default' | 'destructive' {
    switch (severity) {
      case ErrorSeverity.INFO:
      case ErrorSeverity.WARNING:
        return 'default';
      case ErrorSeverity.ERROR:
      case ErrorSeverity.CRITICAL:
      default:
        return 'destructive';
    }
  }
  
  /**
   * Get appropriate title based on severity
   */
  private getSeverityTitle(severity: ErrorSeverity): string {
    switch (severity) {
      case ErrorSeverity.INFO:
        return 'Information';
      case ErrorSeverity.WARNING:
        return 'Warning';
      case ErrorSeverity.CRITICAL:
        return 'Critical Error';
      case ErrorSeverity.ERROR:
      default:
        return 'Error';
    }
  }
}

/**
 * Hook for using error handler with appropriate component context
 */
export const useErrorHandler = (componentName: string) => {
  return new ErrorHandler(componentName);
};

/**
 * TEMPORARY COMPATIBILITY FUNCTION
 * Legacy error handling function to maintain backward compatibility
 * This will be removed in a future update once all code is migrated to the ErrorHandler class
 */
export const handleError = (
  error: unknown, 
  message: string = "An error occurred", 
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  context: ErrorContext = {},
  showToast: boolean = true
): Error => {
  // Create a temporary error handler instance
  const tempHandler = new ErrorHandler(context.source || 'legacy');
  
  // Convert the old context format to the new metadata format
  const metadata: ErrorMetadata = {
    userId: context.userId,
    organizationId: context.organizationId,
    category: context.category,
    operationName: context.operation,
    ...context.additionalInfo,
  };
  
  // Use the new error handler
  return tempHandler.handleError(error, message, metadata, showToast, severity);
};

/**
 * TEMPORARY COMPATIBILITY FUNCTION
 * Legacy async error handler for backward compatibility
 */
export const asyncErrorHandler = async <T>(
  promise: Promise<T>,
  errorMessage: string = "Operation failed",
  context: ErrorContext = {},
  showToast: boolean = true
): Promise<[T | null, Error | null]> => {
  try {
    const data = await promise;
    return [data, null];
  } catch (error) {
    const handledError = handleError(error, errorMessage, ErrorSeverity.ERROR, context, showToast);
    return [null, handledError];
  }
};

/**
 * Global error handler for uncaught exceptions
 */
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    const globalHandler = new ErrorHandler('GlobalErrorHandler');
    globalHandler.handleError(
      event.error || new Error(event.message),
      'Uncaught error',
      {
        category: ErrorCategory.UNEXPECTED,
        sourceFile: event.filename,
        lineNumber: event.lineno,
        columnNumber: event.colno
      },
      true,
      ErrorSeverity.ERROR
    );
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    const globalHandler = new ErrorHandler('GlobalPromiseHandler');
    globalHandler.handleError(
      event.reason || new Error('Unhandled promise rejection'),
      'Unhandled promise rejection',
      {
        category: ErrorCategory.UNEXPECTED
      },
      true,
      ErrorSeverity.ERROR
    );
  });
}

// Type declaration for window error reporting service
declare global {
  interface Window {
    errorReportingService?: {
      captureException: (
        error: Error,
        options: {
          extra?: Record<string, unknown>;
          tags?: Record<string, string>;
          level?: string;
          contexts?: Record<string, Record<string, unknown>>;
          user?: {
            id?: string;
            email?: string;
            username?: string;
            role?: string;
            organizationId?: string;
            [key: string]: unknown;
          };
        }
      ) => void;
      captureMessage?: (
        message: string,
        severity?: ErrorSeverity,
        options?: {
          extra?: Record<string, unknown>;
          tags?: Record<string, string>;
          level?: string;
          contexts?: Record<string, Record<string, unknown>>;
          user?: {
            id?: string;
            email?: string;
            username?: string;
            role?: string;
            organizationId?: string;
            [key: string]: unknown;
          };
        }
      ) => void;
      setUser?: (
        user: {
          id?: string;
          email?: string;
          role?: string;
          organizationId?: string;
          [key: string]: unknown;
        } | null
      ) => void;
      addBreadcrumb?: (
        category: string,
        message: string,
        data?: Record<string, unknown>,
        level?: ErrorSeverity
      ) => void;
    };
  }
}
